import {
	IModel,
	db,
	IIdentifiable,
	VIPUser,
	IVIPUser,
	IVIPUserOptions,
	VIPContact,
	IVIPContact,
	IVIPContactOptions,
	VIPProject,
	IVIPProject,
	IVIPProjectOptions,
} from "@/loader";

export type VIPCompanyFields = "id" | "fk_logo" | "fk_manager" | "fk_contact" | "zipcode" | "country" | "address1" | "address2" | "city" | "name";

/**
 * Company Base Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPCompanyBase extends IIdentifiable {
	/** Id */
	id?: number;
	/** Logo ID */
	fk_logo?: number | null;
	/** Name */
	name?: string;
	/** Address1 */
	address1?: string | null;
	/** Address2 */
	address2?: string | null;
	/** Zipcode */
	zipcode?: string | null;
	/** City */
	city?: string | null;
	/** Country */
	country?: string | null;
	/** Manager ID */
	fk_manager?: number | null;
	/** Contact ID */
	fk_contact?: number | null;

	/** Manager */
	manager?: IVIPUser | null;
	/** Contact */
	contact?: IVIPContact | null;

	/** Projects */
	projects?: IVIPProject[];
	/** Contacts */
	contacts?: IVIPContact[];
}

/**
 * Company Base Options Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPCompanyOptions {
	/** Indicates whether the Id should be included or not. Default true. */
	id?: boolean;
	/** Indicates whether the Logo ID should be included or not. Default true. */
	fk_logo?: boolean;
	/** Indicates whether the Name should be included or not. Default true. */
	name?: boolean;
	/** Indicates whether the Address1 should be included or not. Default true. */
	address1?: boolean;
	/** Indicates whether the Address2 should be included or not. Default true. */
	address2?: boolean;
	/** Indicates whether the Zipcode should be included or not. Default true. */
	zipcode?: boolean;
	/** Indicates whether the City should be included or not. Default true. */
	city?: boolean;
	/** Indicates whether the Country should be included or not. Default true. */
	country?: boolean;
	/** Indicates whether the Manager ID should be included or not. Default true. */
	fk_manager?: boolean;
	/** Indicates whether the Contact ID should be included or not. Default true. */
	fk_contact?: boolean;
	/** Indicates whether the Manager should be included or not. Default false. */
	manager?: boolean | IVIPUserOptions;
	/** Indicates whether the Contact should be included or not. Default false. */
	contact?: boolean | IVIPContactOptions;
	/** Indicates whether the Projects should be included or not. Default false. */
	projects?: boolean | IVIPProjectOptions;
	/** Indicates whether the Contacts should be included or not. Default false. */
	contacts?: boolean | IVIPContactOptions;
}

/**
 * Company Base Class Logic.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export class VIPCompanyBase implements IVIPCompanyBase, IModel<IVIPCompanyBase> {
	/** Id */
	public id!: number;
	/** Logo ID */
	public fk_logo!: number | null;
	/** Name */
	public name!: string;
	/** Address1 */
	public address1!: string | null;
	/** Address2 */
	public address2!: string | null;
	/** Zipcode */
	public zipcode!: string | null;
	/** City */
	public city!: string | null;
	/** Country */
	public country!: string | null;
	/** Manager ID */
	public fk_manager!: number | null;
	/** Contact ID */
	public fk_contact!: number | null;

	/**
	 * Get Manager
	 */
	public get manager(): VIPUser | null {
		return this.fk_manager && db.users.get(this.fk_manager) || null;
	}

	/**
	 * Set Manager
	 */
	public set manager(value: VIPUser | null) {
		this.fk_manager = Number.nullable(value?.id);
	}

	/**
	 * Get Contact
	 */
	public get contact(): VIPContact | null {
		return this.fk_contact && db.contacts.get(this.fk_contact) || null;
	}

	/**
	 * Set Contact
	 */
	public set contact(value: VIPContact | null) {
		this.fk_contact = Number.nullable(value?.id);
	}

	/**
	 * Get Projects
	 */
	public get projects(): VIPProject[] {
		return db.projects.list.filter(x => x.fk_company === this.id);
	}

	/**
	 * Get Contacts
	 */
	public get contacts(): VIPContact[] {
		return db.contacts.list.filter(x => x.fk_company === this.id);
	}

	/**
	 * Constructor.
	 * @param data
	 */
	constructor (data: IVIPCompanyBase = {}) {
		this.load(data);
	}

	/**
	 * Set all properties with the data provided or a default value.
	 *
	 * All properties will be set, either with the value provided or with their default value.
	 *
	 * If you want to set only a subset of this instance's properties, use {@link VIPCompanyBase.update} instead.
	 *
	 * @param data
	 */
	public load(data: IVIPCompanyBase) {
		this.id = Number.orDefault(data.id);
		this.fk_logo = Number.nullable(data.fk_logo);
		this.name = String.orDefault(data.name);
		this.address1 = String.nullable(data.address1);
		this.address2 = String.nullable(data.address2);
		this.zipcode = String.nullable(data.zipcode);
		this.city = String.nullable(data.city);
		this.country = String.nullable(data.country);
		this.fk_manager = Number.nullable(data.fk_manager);
		this.fk_contact = Number.nullable(data.fk_contact);
		
		if (data.manager) {
			db.users.insertOrUpdate(data.manager);
		}
		if (data.contact) {
			db.contacts.insertOrUpdate(data.contact);
		}
		
		if (Array.isArray(data.projects) && data.projects.length) {
			db.projects.insertOrUpdateMany(data.projects);
		}
		if (Array.isArray(data.contacts) && data.contacts.length) {
			db.contacts.insertOrUpdateMany(data.contacts);
		}
	}

	/**
	 * Set the properties with the data provided.
	 *
	 * Only the properties defined in data will be updated. Falsy values will be treated, only `undefined` is not.
	 *
	 * If you want to redefine all properties, use {@link VIPCompanyBase.load} instead.
	 *
	 * @param data
	 */
	public update(data: IVIPCompanyBase) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.fk_logo !== undefined) {
			this.fk_logo = Number.nullable(data.fk_logo);
		}
		if (data.name !== undefined) {
			this.name = String.orDefault(data.name);
		}
		if (data.address1 !== undefined) {
			this.address1 = String.nullable(data.address1);
		}
		if (data.address2 !== undefined) {
			this.address2 = String.nullable(data.address2);
		}
		if (data.zipcode !== undefined) {
			this.zipcode = String.nullable(data.zipcode);
		}
		if (data.city !== undefined) {
			this.city = String.nullable(data.city);
		}
		if (data.country !== undefined) {
			this.country = String.nullable(data.country);
		}
		if (data.fk_manager !== undefined) {
			this.fk_manager = Number.nullable(data.fk_manager);
		}
		if (data.fk_contact !== undefined) {
			this.fk_contact = Number.nullable(data.fk_contact);
		}
		
		if (data.manager) {
			db.users.insertOrUpdate(data.manager);
		}
		if (data.contact) {
			db.contacts.insertOrUpdate(data.contact);
		}
		
		if (Array.isArray(data.projects) && data.projects.length) {
			db.projects.insertOrUpdateMany(data.projects);
		}
		if (Array.isArray(data.contacts) && data.contacts.length) {
			db.contacts.insertOrUpdateMany(data.contacts);
		}
	}
}
